import { Fragment, useRef, useState } from "react";
import { boyaOkumaEtiketi, type BoyaOkumaKarti, type GelenHedef } from "@oasis/domain";
import { Band, Buton, Etiket } from "@/components/parcalar";
import { AltBaslik, Nokta, UyariSatiri } from "@/components/belge-okuma-bolumu";
import { BoyaFinalAlani } from "@/components/boya-raporlari";
import { api } from "@/lib/api";
import {
  BOYA_UCU,
  OKUMA_UCU,
  boyaDegerMetni,
  boyaFinalOneriMetni,
  boyaFormu,
  boyaIkinciMetni,
  boyaKapsamMetni,
  boyaKarsilastirmaSatirlari,
  boyaKartAlanlari,
  boyaKolonMetni,
  boyaSayfalari,
  boyaYerMetni,
  okumaDugmesi,
  okumaEylemleri,
} from "@/lib/belgeler";
import { boyaEtkileri, type BoyaFinalSecimi } from "@/lib/boya-raporu";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { goruntuleyiciLinki } from "@/lib/goruntuleyici";
import { dosyaYukle, tarihSaat } from "@/lib/veri";

/**
 * D-074 Faz 5 · PR 25: Documents kartının boya raporu "Document reading" bölümü (prototip belgeler--boya-okuma, kart 1-3; `SatinAlmaOkumaBolumu` iskeleti). Okuma yalnız yerin, kuralların ve
 * final önerisinin kullandığı değerleri gösterir (sözleşme §16): riser no'ları, hat SN'leri, tarihler, açıklamada anılan riserlar, imza ve test bölümü doluluğu, katlar ve partiler, DFT
 * kolonlarının ortalaması ve en azı. Final önerisi yalnız cümledir: final sorusu her zaman seçimsiz açılır; OASIS final atamaz. DFT kolonu raporun çiftinin dışındaki bir riseri
 * adlandırıyorsa kişi kolonun riserini seçer (BY-07). "Use the reading" kartın bugünkü tür+yer kaydını çağırır (`yerKaydet`, POST :id/tur; satırın hedefi yazılır ve okuma yeniden karşılaştırılır) ve Pages'i doldurur; final seçimsiz kalır, hiçbir iş kaydı yazılmaz. Dosyalanmış iki riserli raporda
 * "Also file for {b}" aynı PDF'i ve sayfaları ikinci riser için bugünkü Finishing yazımından dosyalar; final orada kişinin seçimidir. `salt` kapanmış satır içindir. Okuma excel'inin adı
 * kartta yoktur.
 */

const ETIKET_SINIFI = "text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3";
const TABLO_BASI = "px-2 py-1.5 text-left text-xs font-medium text-ink-3";
const TABLO_HUCRESI = "px-2 py-1.5 align-top [overflow-wrap:anywhere]";

const aralik = (s: { bas: number; son: number }) => (s.bas === s.son ? String(s.bas) : `${s.bas}-${s.son}`);

export interface BoyaFormYardimi {
  /** "Use the reading": dosyalama formunun yeri (riser; final yok) */
  kullan: (h: GelenHedef) => void;
  /** "Use the reading": Pages alanı (rapor PDF'in bir kısmıysa) */
  sayfalar: (s: string) => void;
}

export function BoyaOkumaBolumu({ satirId, k, onYenile, salt, form }: { satirId: string; k: BoyaOkumaKarti; onYenile: () => void; salt?: boolean; hedef?: GelenHedef | null; form?: BoyaFormYardimi }) {
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [degismedi, setDegismedi] = useState(false);
  const [secim, setSecim] = useState<BoyaFinalSecimi>("");
  const secici = useRef<HTMLInputElement>(null);
  const o = k.okuma;
  const ey = okumaEylemleri(k, salt);
  const yardim = salt ? undefined : form;

  const calistir = async (anahtar: string, f: () => Promise<unknown>) => {
    setMesgul(anahtar);
    setHata(null);
    setDegismedi(false);
    try {
      await f();
      onYenile();
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(null);
    }
  };

  const sayfaLinki = (sayfa: number | null) =>
    o && sayfa !== null ? <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa })} target="_blank" rel="noreferrer">p.{sayfa}</a></> : null;
  const karsilastirmaSatirlari = boyaKarsilastirmaSatirlari(k);
  const tumUyarilar = k.karsilastirma?.uyarilar ?? [];
  const dftKodlari = new Set(["BY-07", "BY-02"]);
  const sariGri = tumUyarilar.length > 0 && tumUyarilar.every((u) => u.renk !== "kirmizi");
  const sayfalar = boyaSayfalari(k);
  const formYeri = boyaFormu(k);
  const yerKuyrukta = Boolean(formYeri.rn);
  const kapsam = k.yer?.kapsam ?? [];
  const ikinci = k.ikinci;
  const ikinciOneri = ikinci?.rn ? k.finalOnerileri.find((f) => f.rn === ikinci.rn) ?? null : null;

  return (
    <div data-bolum="boya-okuma">
      <h3 className={cn("mb-1.5", ETIKET_SINIFI)}>{cevir("Document reading")}</h3>
      {k.is.durum === "sirada" && <p className="mt-1 text-sm">{cevir("In the reading queue · place {0}", k.is.siraNo ?? "—")}</p>}
      {k.is.durum === "donusturuluyor" && <p className="mt-1 text-sm">{cevir("The converter is reading this file.")}</p>}
      {k.is.durum === "hata" && (
        <Band tip="not" className="mt-1.5">
          {cevir("Could not read: {0}", cevir(k.is.hata ?? ""))}
          {k.is.hataTs && <span className="mt-0.5 block text-xs">{tarihSaat(k.is.hataTs)}</span>}
        </Band>
      )}
      {!o && !k.is.durum && !salt && (
        <p className="mt-1 text-sm text-ink-2">{cevir("OASIS reads the coating report for you to check: riser numbers, line SNs, date, coats, batches and DFT. Nothing is filed and no final is chosen.")}</p>
      )}
      {k.engel && !salt && <Band tip="not" className="mt-1.5">{cevir(k.engel)}</Band>}

      {o && (
        <>
          <p className="text-xs text-ink-3">
            {[
              o.sayfalar ? cevir("Coating report p.{0}", aralik(o.sayfalar)) : null,
              k.kod ? cevir("Reading job {0}", k.kod) : null,
              cevir("{0} coats", o.katSayisi),
              cevir("{0} DFT columns read", o.dftSayisi),
            ].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-ink-3">
            {o.kaynak.tur === "donusturucu"
              ? cevir("Read by the converter on {0} · engine {1}", tarihSaat(o.kaynak.ts), o.motor)
              : cevir("Reading excel uploaded by {0} on {1} · engine {2}", o.kaynak.ad, tarihSaat(o.kaynak.ts), o.motor)}
          </p>

          <ul className="mt-2 space-y-0.5 text-sm" data-bolum="boya-baslik">
            {boyaKartAlanlari(k).map((d) => (
              <li key={d.alan} className="flex gap-2">
                <Nokta k={d.kesinlik} />
                <span className="w-[10rem] shrink-0 text-ink-2">{cevir(boyaOkumaEtiketi(d.alan))}</span>
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  {d.deger ? boyaDegerMetni(d.alan, d) : <span className="text-ink-3">{cevir("not read")}</span>}
                  {sayfaLinki(d.sayfa)}
                </span>
              </li>
            ))}
          </ul>

          {k.katlar.length > 0 && (
            <>
              <AltBaslik>{cevir("Coats")}</AltBaslik>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" data-bolum="bl-kars">
                  <thead><tr className="border-b border-line">
                    <th className={TABLO_BASI}>{cevir("Row")}</th>
                    <th className={TABLO_BASI}>{cevir("Coat")}</th>
                    <th className={TABLO_BASI}>{cevir("Product")}</th>
                    <th className={TABLO_BASI}>{cevir("Batch A / B")}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-line">
                    {k.katlar.map((s) => {
                      const v = (a: string) => s.degerler.find((d) => d.alan === a);
                      const kat = v("kat")?.deger;
                      return (
                        <tr key={s.no} data-kat={s.no}>
                          <td className={TABLO_HUCRESI}><span className="font-mono">{s.no}</span></td>
                          <td className={TABLO_HUCRESI}><Nokta k={v("kat")?.kesinlik ?? "bos"} /> {kat ? cevir(kat) : "—"}</td>
                          <td className={TABLO_HUCRESI}>{v("urun")?.deger ?? "—"}</td>
                          <td className={TABLO_HUCRESI}>{[v("parti_a")?.deger, v("parti_b")?.deger].filter(Boolean).join(" / ") || "—"}{sayfaLinki(v("kat")?.sayfa ?? null)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {k.dftler.length > 0 && (
            <>
              <AltBaslik>{cevir("DFT columns")}</AltBaslik>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" data-bolum="bl-kars">
                  <thead><tr className="border-b border-line">
                    <th className={TABLO_BASI}>{cevir("Column")}</th>
                    <th className={TABLO_BASI}>{cevir("On the report")}</th>
                    <th className={TABLO_BASI}>{cevir("Riser")}</th>
                    <th className={TABLO_BASI}>{cevir("Result")}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-line">
                    {k.dftler.map((s) => {
                      const v = (a: string) => s.degerler.find((d) => d.alan === a)?.deger ?? null;
                      const c = s.kolon;
                      const tutarsiz = s.uyarilar.some((u) => u.kod === "BY-02");
                      const sec = c?.durum === "secilmedi" && k.eylemler.dftSec && !salt;
                      return (
                        <tr key={s.no} data-dft={s.no}>
                          <td className={TABLO_HUCRESI}><span className="font-mono">{s.no}</span></td>
                          <td className={TABLO_HUCRESI}>
                            {v("kolon_etiketi") ?? "—"}
                            <span className="block text-xs text-ink-3">{cevir("average {0} µm · minimum {1} µm", v("ortalama") ?? "—", v("en_az") ?? "—")}</span>
                          </td>
                          <td className={TABLO_HUCRESI}>
                            {boyaKolonMetni(c)}
                            {sec && (
                              <span className="mt-1 flex flex-wrap gap-1">
                                {kapsam.map((rn) => (
                                  <Buton key={rn} kucuk disabled={mesgul !== null} mesgul={mesgul === `dft-${s.no}-${rn}`} onClick={() => calistir(`dft-${s.no}-${rn}`, () => api.post(BOYA_UCU.dftSec(satirId), { sira: s.no, rn }))}>{rn}</Buton>
                                ))}
                              </span>
                            )}
                          </td>
                          <td className={TABLO_HUCRESI}>
                            {c?.durum === "secilmedi" || c?.durum === "supheli" ? cevir("not checked until you choose the riser") : tutarsiz ? cevir("minimum above average") : cevir("checked")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {kapsam.length === 2 && <p className="mt-1 text-xs text-ink-3">{cevir("The label of a DFT column does not show who owns the report; a column naming a riser outside the pair is checked only after you choose its riser ({0} or {1}).", kapsam[0]!, kapsam[1]!)}</p>}
            </>
          )}

          {k.yer && (
            <div data-bolum="boya-yer">
              <AltBaslik>{cevir("Where it goes")}</AltBaslik>
              <p className="text-sm">
                {boyaYerMetni(k)}
                {k.yer.durum === "kisi" && <Etiket tone="ok" className="ml-1.5">{cevir("your choice")}</Etiket>}
                {k.yer.durum === "oneri" && <Etiket tone="info" className="ml-1.5">{cevir("proposed")}</Etiket>}
              </p>
              {boyaKapsamMetni(kapsam) && <p className="text-xs text-ink-3">{boyaKapsamMetni(kapsam)}</p>}
              {ikinci?.rn && ikinci.durum === "dosyalanmadi" && <p className="text-xs text-ink-3">{boyaIkinciMetni("dosyalanmadi", ikinci.rn)}</p>}
              {ikinci?.durum === "ikinci_yok" && <p className="text-xs text-ink-3">{boyaIkinciMetni("ikinci_yok", null, ikinci.sayi)}</p>}
            </div>
          )}

          {k.finalOnerileri.length > 0 && (
            <div data-bolum="boya-final-onerisi">
              <AltBaslik>{cevir("Final suggestion")}</AltBaslik>
              <ul className="space-y-0.5 text-sm">
                {k.finalOnerileri.map((f) => <li key={f.rn}><span className="mr-1.5 font-mono">{f.rn}</span>{boyaFinalOneriMetni(f)}</li>)}
              </ul>
              <p className="mt-1 text-xs text-ink-3">{cevir("A suggestion only. You choose Final or Not final when you file; OASIS never sets a final.")}</p>
            </div>
          )}

          {yardim && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-bolum="okumayi-kullan">
              <Buton kucuk disabled={mesgul !== null || !yerKuyrukta} title={!yerKuyrukta ? boyaYerMetni(k) : undefined} onClick={() => {
                yardim.kullan({ tur: "boya", rn: formYeri.rn });
                if (sayfalar) yardim.sayfalar(sayfalar);
              }}>{cevir("Use the reading")}</Buton>
              <span className="w-full text-xs text-ink-3">
                {cevir("“Use the reading” fills Riser with the proposed riser and Pages with the pages of the report when it is part of the PDF. The final stays your choice. Nothing is filed until you confirm.")}
              </span>
            </div>
          )}
          {yardim && sariGri && (
            <Band tip="not" className="mt-2">
              <span className="block font-medium">{cevir("Before you file")}</span>
              <span className="block">{cevir("Amber and grey alerts do not stop filing; they stay with the reading.")}</span>
            </Band>
          )}

          {salt && ikinci && ikinci.durum !== "yok" && ikinci.rn && ikinci.durum !== "dosyalanmadi" && (
            <div data-bolum="boya-ikinci">
              <AltBaslik>{cevir("Also file for {0}", ikinci.rn)}</AltBaslik>
              {ikinci.durum === "eklendi" && ikinci.onay
                ? <p className="text-sm">{cevir("Also filed for {0} by {1} on {2} · {3}", ikinci.rn, ikinci.onay.kim, tarihSaat(ikinci.onay.ts), ikinci.onay.final ? cevir("Final") : cevir("Not final"))}</p>
                : <p className="text-sm">{ikinci.durum === "engel" && ikinci.engel ? cevir(ikinci.engel) : boyaIkinciMetni(ikinci.durum, ikinci.rn, ikinci.sayi)}</p>}
              {ikinci.durum === "eklenebilir" && k.eylemler.ikinci && (
                <>
                  <BoyaFinalAlani deger={secim} onChange={setSecim} disabled={mesgul !== null} />
                  {ikinciOneri && boyaFinalOneriMetni(ikinciOneri) && <p className="mt-1 text-xs text-ink-3">{boyaFinalOneriMetni(ikinciOneri)}</p>}
                  {secim !== "" && (
                    <ul className="mt-1.5 list-disc pl-5 text-xs text-ink-2">
                      {boyaEtkileri({ rn: ikinci.rn, secim, mevcutFinalAd: ikinci.mevcutFinal?.dosya ?? null, asmVar: ikinci.asmVar }).map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                  )}
                  <p className="mt-1 text-xs text-ink-3">{cevir("{0} gets the same PDF and pages through today's Finishing upload. The filing of {1} does not change.", ikinci.rn, k.yer?.yer?.tur === "boya" ? k.yer.yer.rn : "—")}</p>
                  <Buton kucuk tur="birincil" className="mt-1.5" disabled={mesgul !== null || secim === ""} mesgul={mesgul === "ikinci"} onClick={() => calistir("ikinci", () => api.post(BOYA_UCU.ikinci(satirId), { final: secim === "evet", oncekiFinal: ikinci.mevcutFinal?.anahtar ?? "" }))}>
                    {cevir("File for {0} too", ikinci.rn)}
                  </Buton>
                </>
              )}
            </div>
          )}

          <p className="mt-1 text-xs text-ink-3">
            {cevir("Not read: the work description (only the riser numbers in it), colour, humidity, temperatures and dew point, the DFT maximum and the specification DFT, deviation, number of readings, evaluation, tests and the other signature boxes.")}
          </p>
        </>
      )}

      {o && k.karsilastirma && (
        <div data-bolum="karsilastirma">
          <AltBaslik>{cevir("Compared with OASIS")}</AltBaslik>
          {k.karsilastirma.ts && <p className="text-xs text-ink-3">{tarihSaat(k.karsilastirma.ts)}{k.kod && <> · {cevir("Reading job {0}", k.kod)}</>}</p>}
          {karsilastirmaSatirlari.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-sm">
              {karsilastirmaSatirlari.map((n, i) => (
                <li key={i} className="flex gap-2">
                  <Nokta k={n.karsilastirildi ? "kesin" : n.suphe ? "suphe" : "bos"} />
                  <span className="min-w-0">{n.metin}</span>
                </li>
              ))}
            </ul>
          )}
          <AltBaslik>{cevir("Alerts from the reading")}</AltBaslik>
          {!k.karsilastirma.karsilastirildi && tumUyarilar.length === 0
            ? <Band tip="not">{salt ? cevir("This reading was not compared with OASIS.") : cevir("This reading was not compared with OASIS. Use Compare again to compare it.")}</Band>
            : tumUyarilar.length === 0
              ? <p className="text-sm text-ink-2">{cevir("No alerts from the comparison.")}</p>
              : (
                <ul className="space-y-1.5">
                  {tumUyarilar.map((u) => {
                    const kolon = dftKodlari.has(u.kod) ? k.dftler.find((s) => s.uyarilar.some((x) => x.id === u.id)) : undefined;
                    return (
                      <Fragment key={u.id}>
                        {kolon && <li className="list-none text-xs font-medium text-ink-2">{cevir("DFT column {0}: {1}", kolon.no, kolon.kolon?.etiket ?? "—")}</li>}
                        <UyariSatiri u={u} yer={null} salt={salt} mesgul={mesgul} calistir={calistir} />
                      </Fragment>
                    );
                  })}
                </ul>
              )}
          {k.karsilastirma.bulgular.length > 0 && (
            <>
              <AltBaslik>{cevir("Notes from the reading")}</AltBaslik>
              <ul className="space-y-0.5 text-xs text-ink-3">
                {k.karsilastirma.bulgular.map((b, i) => <li key={i}><span className="mr-1.5 font-mono">{b.kod}</span>{b.mesaj}</li>)}
              </ul>
            </>
          )}
          {degismedi && <p className="mt-1.5 text-sm text-ink-2">{cevir("No change: the comparison gives the same alerts.")}</p>}
          <p className="mt-2 flex flex-wrap gap-3 text-xs text-ink-3">
            <span className="inline-flex items-center gap-1"><Nokta k="kesin" />{cevir("certain")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="suphe" />{cevir("check")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="bos" />{cevir("not read")}</span>
          </p>
        </div>
      )}

      {(ey.gonder || ey.cikar || ey.girdi || ey.karsilastir) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {ey.gonder && (
            <Buton kucuk tur="birincil" disabled={mesgul !== null} mesgul={mesgul === "ekle"} onClick={() => calistir("ekle", () => api.post(BOYA_UCU.sira(satirId), { islem: "ekle" }))}>
              {cevir(okumaDugmesi(k))}
            </Buton>
          )}
          {ey.karsilastir && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "karsilastir"} onClick={() => calistir("karsilastir", async () => {
              const r = await api.post<{ ok: true; degisti: boolean }>(OKUMA_UCU.karsilastir(satirId));
              if (!r.degisti) setDegismedi(true);
            })}>
              {cevir("Compare again")}
            </Buton>
          )}
          {ey.cikar && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "cikar"} onClick={() => calistir("cikar", () => api.post(BOYA_UCU.sira(satirId), { islem: "cikar" }))}>
              {cevir("Remove from reading queue")}
            </Buton>
          )}
          {ey.girdi && (
            <>
              <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "girdi"} onClick={() => secici.current?.click()}>{cevir("Upload the reading excel by hand")}</Buton>
              <input
                ref={secici}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void calistir("girdi", () => dosyaYukle(BOYA_UCU.girdi(satirId), f, {}, {}, "excel"));
                }}
              />
            </>
          )}
        </div>
      )}
      {hata && <Band tip="duvar" className="mt-2">{hata}</Band>}
    </div>
  );
}
